import api from './api';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.designinfra.co.in/api';

// The API base minus the trailing `/api` — images are served from
// {host}/storage/{path}, not from under the API prefix.
const STORAGE_BASE = `${API_URL.replace(/\/api\/?$/, '')}/storage`;

/**
 * Expand a stored image path (`projects/a1b2c3.jpg`) into a URL.
 * Returns null when the project has no photo, so callers can branch on it.
 */
export const projectImageUrl = (image) =>
  image ? `${STORAGE_BASE}/${image}` : null;

const toBool = (value) => Boolean(Number(value));

/**
 * Normalize a raw API project to the shape used by the UI.
 * API fields: id, title, client_name, location, category, capacity, year,
 * description, image, is_featured, is_active
 */
const normalizeProject = (raw) => ({
  id: raw.id,
  title: raw.title ?? '',
  // Nullable on the API; '' keeps the inputs controlled.
  clientName: raw.client_name ?? '',
  location: raw.location ?? '',
  category: raw.category ?? '',
  capacity: raw.capacity ?? '',
  year: raw.year ?? '',
  description: raw.description ?? '',
  image: raw.image ?? null,
  isFeatured: toBool(raw.is_featured),
  isActive: toBool(raw.is_active),
  createdAt: raw.created_at ?? null,
});

/**
 * Build a multipart/form-data payload from UI values.
 * Only appends `image` when a real File is provided, so saving without
 * touching the photo leaves the existing one in place.
 */
const buildFormData = ({
  title, clientName, location, category, capacity, year, description, image, isFeatured, isActive,
}) => {
  const fd = new FormData();
  fd.append('title', title ?? '');
  fd.append('client_name', clientName ?? '');
  fd.append('location', location ?? '');
  fd.append('category', category ?? '');
  fd.append('capacity', capacity ?? '');
  fd.append('year', year ?? '');
  fd.append('description', description ?? '');
  fd.append('is_featured', isFeatured ? '1' : '0');
  fd.append('is_active', isActive ? '1' : '0');
  if (image instanceof File) {
    fd.append('image', image);
  }
  return fd;
};

export const projectService = {
  // No `search` param — the backend implements none for this module,
  // so ProjectsPage filters the list locally.
  getAll: async (params = {}) => {
    const response = await api.get('/projects', { params });
    const raw = response.data;
    const list = Array.isArray(raw) ? raw : (raw.data ?? []);
    const projects = list.map(normalizeProject);
    return { data: { projects, total: projects.length } };
  },

  getById: async (id) => {
    const response = await api.get(`/projects/${id}`);
    return { data: normalizeProject(response.data?.data ?? response.data) };
  },

  create: async (data) => {
    const response = await api.post('/projects', buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { data: normalizeProject(response.data?.data ?? response.data) };
  },

  // POST, not PUT — PHP does not parse multipart on PUT, so the file would be lost.
  update: async (id, data) => {
    const response = await api.post(`/projects/update/${id}`, buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { data: normalizeProject(response.data?.data ?? response.data) };
  },

  delete: async (id) => api.delete(`/projects/${id}`),

  toggleStatus: async (id) => api.patch(`/projects/toggle/${id}`),

  toggleFeatured: async (id) => api.patch(`/projects/toggle-featured/${id}`),
};

export default projectService;
